import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useState } from 'react'
import { Helmet } from 'react-helmet'
import { setPath, setUrlParam } from '../../../utils/url'
import { URL } from '../../constants/url'
import { useHistory } from 'react-router-dom'
import { secToHourMinute } from '../../../utils/date'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'

interface Card {
  key: string
  labelType: string
  icon: string
  title: string
  description: string
  image: string
  breadcrumb: {
    icon: string
    textList: string[]
  }
  buttonText: string
  url: string
}

interface DisplayList {
  key: string
  title: string
  description: string
  color: string
  bgColor: string
  filter: string
  icon: string
  card: Card[]
}

interface Status {
  key: string
  title: string
  type: string
  value: number
  prefix: string
  suffix: string
  url: string
}

const DISPLAY_LIST: DisplayList[] = [
  {
    key: "self",
    title: "自分自身を見たい",
    description: "自分の仕分け・申請・働き方を確認する",
    color: "#1C48B8",
    bgColor: "#d9e3f8",
    filter: "brightness(0) saturate(100%) invert(19%) sepia(100%) saturate(1757%) hue-rotate(215deg) brightness(100%) contrast(100%)",
    icon: "/static/images/dashboard/icon_1.png",
    card: [
      {
        key: "task_labeling_self",
        labelType: "edit",
        icon: "/static/images/dashboard/icon_2.png",
        title: "自分の仕分けをする",
        description: "その日の作業データを見ながら、プロジェクト・業務を割り当てます。",
        image: "/static/images/dashboard/image_1_1.png",
        breadcrumb: {
          icon: "hammer",
          textList: ["工数管理", "業務仕分けボード"],
        },
        buttonText: "ボードを開く",
        url: setPath(URL.TASK_LABELING_SELF.path, 'ja'),
      },
      {
        key: "task_approval_self",
        labelType: "edit",
        icon: "/static/images/dashboard/icon_3.png",
        title: "自分の業務仕分けを申請する",
        description: "月カレンダーで日ごとの仕分け率と承認状況を確認し、まとめて申請します。",
        image: "/static/images/dashboard/image_1_2.png",
        breadcrumb: {
          icon: "hammer",
          textList: ["工数管理", "申請状況"],
        },
        buttonText: "申請画面へ",
        url: setPath(URL.TASK_APPROVAL_SELF.path, 'ja'),
      },
      {
        key: "data_summary",
        labelType: "report",
        icon: "/static/images/dashboard/icon_4.png",
        title: "自分の働き方をみたい",
        description: "勤務時間・作業時間・操作量の推移と、分類別の作業割合を確認できます。",
        image: "/static/images/dashboard/image_1_3.png",
        breadcrumb: {
          icon: "child",
          textList: ["セルフ", "サマリー"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.DATA_SUMMARY.path, 'ja'),
      },
    ],
  },
  {
    key: "project",
    title: "プロジェクト単位でみたい",
    description: "案件ごとのコスト・時間・業務割合を把握する",
    color: "#8A6008",
    bgColor: "#FDF3DD",
    filter: "brightness(0) saturate(100%) invert(31%) sepia(95%) saturate(698%) hue-rotate(12deg) brightness(98%) contrast(94%)",
    icon: "/static/images/dashboard/icon_5.png",
    card: [
      {
        key: "task",
        labelType: "report",
        icon: "/static/images/dashboard/icon_6.png",
        title: "プロジェクト全体の工数実績をみたい",
        description: "予実・工数時間・利益率を案件横断で一覧。損益の透明性を確認できます。",
        image: "/static/images/dashboard/image_2_1.png",
        breadcrumb: {
          icon: "hammer",
          textList: ["工数管理", "プロジェクトレポート"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.TASK.path, 'ja'),
      },
      {
        key: "task_category_detail",
        labelType: "report",
        icon: "/static/images/dashboard/icon_5.png",
        title: "カテゴリーで工数比率をみたい",
        description: "複数プロジェクトをカテゴリーでまとめ、予実合計と工数比率を可視化します。",
        image: "/static/images/dashboard/image_2_2.png",
        breadcrumb: {
          icon: "hammer",
          textList: ["工数管理", "カテゴリーレポート"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.TASK_CATEGORY_DETAIL.path, 'ja'),
      },
      {
        key: "task_labeling_status",
        labelType: "report",
        icon: "/static/images/dashboard/icon_7.png",
        title: "メンバーの仕分け状況をみたい",
        description: "メンバーごとの仕分け済み/未仕分けを一覧し、仕分け漏れを見つけられます。",
        image: "/static/images/dashboard/image_2_3.png",
        breadcrumb: {
          icon: "hammer",
          textList: ["工数管理", "仕分け状況レポート"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.TASK_LABELING_STATUS.path, 'ja'),
      },
    ],
  },
  {
    key: "member_and_team",
    title: "メンバー・チーム単位でみたい",
    description: "メンバーの働き方・仕分け状況・申請承認を確認する",
    color: "#B92B50",
    bgColor: "#FFECF1",
    filter: "brightness(0) saturate(100%) invert(21%) sepia(68%) saturate(2275%) hue-rotate(322deg) brightness(94%) contrast(91%)",
    icon: "/static/images/dashboard/icon_8.png",
    card: [
      {
        key: "risk_member_list",
        labelType: "report",
        icon: "/static/images/dashboard/icon_9.png",
        title: "メンバーの働き方をみたい",
        description: "メンバー1人ずつ、勤務・作業・操作量を前日比つきで日次/月次に確認できます。",
        image: "/static/images/dashboard/image_3_1.png",
        breadcrumb: {
          icon: "heartbeat",
          textList: ["リスク", "ユーザーレポート"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.RISK_MEMBER_LIST.path, 'ja'),
      },
      {
        key: "task_approval_status",
        labelType: "report",
        icon: "/static/images/dashboard/icon_10.png",
        title: "メンバーの申請承認・業務仕分け率をみたい",
        description: "メンバーx日付のマトリクスで申請ステータスと仕分け率を確認し、まとめて承認できます。",
        image: "/static/images/dashboard/image_3_2.png",
        breadcrumb: {
          icon: "hammer",
          textList: ["工数管理", "承認状況"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.TASK_APPROVAL_STATUS.path, 'ja'),
      },
      {
        key: "risk_team_list",
        labelType: "report",
        icon: "/static/images/dashboard/icon_8.png",
        title: "チームの働き方をみたい",
        description: "チーム平均勤務時間を過去30日と比較し、メンバー別の内訳まで確認できます。",
        image: "/static/images/dashboard/image_3_3.png",
        breadcrumb: {
          icon: "heartbeat",
          textList: ["リスク", "チームレポート"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.RISK_TEAM_LIST.path, 'ja'),
      },
    ],
  },
  {
    key: "organization",
    title: "組織単位でみたい",
    description: "組織のアプリ利用・印刷・働き方の傾向を確認する",
    color: "#0A6B4B",
    bgColor: "#E4F6EF",
    filter: "brightness(0) saturate(100%) invert(30%) sepia(52%) saturate(660%) hue-rotate(109deg) brightness(96%) contrast(96%)",
    icon: "/static/images/dashboard/icon_11.png",
    card: [
      {
        key: "app_analyze",
        labelType: "report",
        icon: "/static/images/dashboard/icon_12.png",
        title: "アプリケーションの利用状況をみたい",
        description: "アプリ別の利用時間割合と利用人数を集計。ツールが定着しているか確認できます。",
        image: "/static/images/dashboard/image_4_1.png",
        breadcrumb: {
          icon: "flask",
          textList: ["組織分析", "アプリケーション分析"],
        },
        buttonText: "レポートを見る",
        url: setPath(URL.APP_ANALYZE.path, 'ja'),
      },
      {
        key: "print",
        labelType: "report",
        icon: "/static/images/dashboard/icon_13.png",
        title: "印刷状況をみたい",
        description: "印刷人数・回数・枚数の推移から、ペーパーレス化の余地を見つけられます。",
        image: "/static/images/dashboard/image_4_2.png",
        breadcrumb: {
          icon: "flask",
          textList: ["組織分析", "ペーパーレス分析"],
        },
        buttonText: "レポートを見る",
        url: setPath(setUrlParam(URL.ANALYZE_REPORT_DETAIL.path, [{ key: 'type', value: 'print' }]), 'ja'),
      },
      {
        key: "work_style",
        labelType: "report",
        icon: "/static/images/dashboard/icon_14.png",
        title: "リモートと出社の働き方の違いをみたい",
        description: "リモート稼働時とオフィス出社時で、操作量・勤務時間・離席回数の差を比較できます。",
        image: "/static/images/dashboard/image_4_3.png",
        breadcrumb: {
          icon: "flask",
          textList: ["組織分析", "ワークスタイル分析"],
        },
        buttonText: "レポートを見る",
        url: setPath(setUrlParam(URL.ANALYZE_REPORT_DETAIL.path, [{ key: 'type', value: 'work_style' }]), 'ja'),
      },
    ],
  },
]

const STATUS_LIST: Status[] = [
  {
    key: "daily_not_labeling_time",
    title: "今日のあなたの未仕分け時間",
    type: "time",
    value: 5400,
    prefix: "",
    suffix: "",
    url: setPath(URL.TASK_LABELING_SELF.path, 'ja'),
  },
  {
    key: "monthly_not_labeling_time",
    title: "今月のあなたの未仕分け時間",
    type: "time",
    value: 37800,
    prefix: "",
    suffix: "",
    url: setPath(URL.TASK_APPROVAL_SELF.path, 'ja'),
  },
  {
    key: "rollback_and_comments",
    title: "あなたへの差戻し、コメント件数",
    type: "number",
    value: 0,
    prefix: "",
    suffix: "件",
    url: setPath(URL.TASK_APPROVAL_SELF.path, 'ja'),
  },
  {
    key: "waiting_for_approval",
    title: "あなたの承認待ち申請件数",
    type: "number",
    value: 20,
    prefix: "",
    suffix: "件",
    url: setPath(URL.TASK_APPROVAL_STATUS.path, 'ja'),
  },
  {
    key: "waiting_for_approval",
    title: "any",
    type: "number",
    value: 10,
    prefix: "",
    suffix: "件",
    url: setPath(URL.TASK_APPROVAL_STATUS.path, 'ja'),
  },
  {
    key: "waiting_for_approval",
    title: "any",
    type: "number",
    value: 0,
    prefix: "",
    suffix: "件",
    url: setPath(URL.TASK_APPROVAL_STATUS.path, 'ja'),
  },
]

const Index: FC = () => {
  const history = useHistory()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isHover, setIsHover] = useState<string>("")

  const buildLabel = (labelType: string) => {
    let text = "レポート"
    let color = "#666666"
    let bgColor = "#FFFFFF"
    if (labelType === "edit") {
      text = "操作"
      color = "#FFFFFF"
      bgColor = "#2F6BF0"
    }
    return (
      <div
        style={{
          position: "absolute",
          top: "-4px",
          right: "0px",
          border: `solid 1px ${color}`,
          borderRadius: "4px",
          padding: "4px 8px",
          fontSize: "10px",
          color: color,
          backgroundColor: bgColor,
        }}
      >
        {text}
      </div>
    )
  }

  const formatValue = (value: number, type: string) => {
    if (type === "time") {
      return secToHourMinute(value)
    }
    return value
  }

  const checkAlert = (value: number, type: string) => {
    if (type === "time") {
      return value >= 60
    }
    return !!value
  }

  const displayStatus = (key: string) => {
    if (
      ["daily_not_labeling_time", "monthly_not_labeling_time", "rollback_and_comments"].includes(key)
      && login.feature_config?.task_approver
    ) {
      return false
    }
    if (
      ["waiting_for_approval"].includes(key)
      && !login.feature_config?.task_approver
    ) {
      return false
    }
    return true
  }

  const buildStatus = (data: Status, last?: boolean) => {
    if (!displayStatus(data.key)) return (<></>)
    const alertColor = checkAlert(data.value, data.type) ? "#FF0000" : "#008800"
    return (
      <div
        style={{
          padding: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "calc((100% - 200px) / 3)",
          borderRight: "solid 1px #CCCCCC",
          borderRadius: last ? "0 10px 10px 0" : "0",
          backgroundColor: isHover === data.key ? `${alertColor}11` : "#FFFFFF",
          cursor: "pointer",
        }}
        onClick={() => history.push(data.url)}
        role="none"
        onMouseEnter={() => setIsHover(data.key)}
        onMouseLeave={() => setIsHover("")}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: alertColor,
            }}
          />
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "2px",
            }}
          >
            <div
              style={{
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              {data.prefix}
              {formatValue(data.value, data.type)}
              {data.suffix}
            </div>
            <div
              style={{
                fontSize: "12px",
              }}
            >
              {data.title}
            </div>
          </div>
        </div>
        <div>
          →
        </div>
      </div>
    )
  }

  const displayArea = (key: string) => {
    if (
      ["project", "member_and_team", "organization"].includes(key)
      && !login.feature_config?.task_approver
      && !login.feature_config?.task_report_view
      && !login.feature_config?.risk_member_report_view
      && !login.feature_config?.risk_team_report_view
      && !login.feature_config?.task_labeling_status_view
      && !login.feature_config?.organizational_analysis_view
    ) {
      return false
    }
    if (
      (key === "task" && !login.feature_config?.task_report_view)
      || (key === "task_category_detail" && !login.feature_config?.task_category_report_view)
      || (key === "task_labeling_status" && !login.feature_config?.task_labeling_status_view)
      || (key === "risk_member_list" && !login.feature_config?.risk_member_report_view)
      || (key === "risk_team_list" && !login.feature_config?.risk_team_report_view)
      || (key === "task_approval_status" && !login.feature_config?.task_approver)
      || (["app_analyze", "print", "work_style"].includes(key) && !login.feature_config?.organizational_analysis_view)
    ) {
      return false
    }
    return true
  }

  const accessDeniedMessages = (key: string) => {
    // オプション未契約
    if (key === "data_summary" && !login.function_enabled_state?.self_management) {
      return ["セルフマネジメント機能", "オプション契約が必要です"]
    }
    if (["risk_member_list", "risk_team_list"].includes(key) && !login.function_enabled_state?.risk_assessment) {
      return ["リスクアセスメント機能", "オプション契約が必要です"]
    }
    if (["app_analyze", "print", "work_style"].includes(key) && !login.function_enabled_state?.organizational_analysis) {
      return ["組織分析機能", "オプション契約が必要です"]
    }
    // 閲覧条件
    if (key === "task" && !login.feature_config?.task_report_view) {
      return ["権限がありません"]
    }
    if (key === "task_category_detail" && !login.feature_config?.task_category_report_view) {
      return ["権限がありません"]
    }
    if (key === "task_labeling_status" && !login.feature_config?.task_labeling_status_view) {
      return ["権限がありません"]
    }
    if (key === "risk_member_list" && !login.feature_config?.risk_member_report_view) {
      return ["権限がありません"]
    }
    if (key === "risk_team_list" && !login.feature_config?.risk_team_report_view) {
      return ["権限がありません"]
    }
    if (key === "task_approval_status" && !login.feature_config?.task_approver) {
      return ["権限がありません"]
    }
    if (["app_analyze", "print", "work_style"].includes(key) && !login.feature_config?.organizational_analysis_view) {
      return ["権限がありません"]
    }
    return []
  }

  const buildCard = (displayList: DisplayList, card: Card) => {
    if (!displayArea(card.key)) return (<></>)
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexFlow: "column",
          gap: "8px",
          backgroundColor: !accessDeniedMessages(card.key).length && isHover === card.key ? `${displayList.bgColor}66` : "#FFFFFF",
          padding: "16px",
          border: !accessDeniedMessages(card.key).length && isHover === card.key ? `solid 1px ${displayList.color}` : "solid 1px #CCCCCC",
          borderRadius: "8px",
          width: "calc((100% - 32px) / 3)",
          cursor: accessDeniedMessages(card.key).length ? "not-allowed" : "pointer",
        }}
        onMouseEnter={() => setIsHover(card.key)}
        onMouseLeave={() => setIsHover("")}
        onClick={() => !accessDeniedMessages(card.key).length && history.push(card.url)}
        role="none"
      >
        {
          !!accessDeniedMessages(card.key).length && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(100, 100, 100, 0.5)",
                zIndex: "1",
                borderRadius: "8px",
                display: "flex",
                flexFlow: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                color: "#FFFFFF",
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              <FontAwesomeIcon
                icon={['fas', 'lock']}
                style={{
                  fontSize: "24px",
                }}
              />
              {
                accessDeniedMessages(card.key).map((v) => (
                  <div>
                    {v}
                  </div>
                ))
              }
            </div>
          )
        }
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            position: "relative",
          }}
        >
          <div
            style={{
              backgroundColor: displayList.bgColor,
              color: displayList.color,
              width: "30px",
              height: "30px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={card.icon}
              alt={card.title}
              style={{
                width: "70%",
                filter: displayList.filter,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "2px",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              {card.title}
            </div>
          </div>
          {buildLabel(card.labelType)}
        </div>
        <div
          style={{
            color: "#666666",
            wordBreak: "break-all",
            fontSize: "14px",
            minHeight: "40px",
          }}
        >
          {card.description}
        </div>
        <div
          style={{
            width: "100%",
          }}
        >
          <img
            src={card.image}
            alt={card.title}
            style={{
              width: "100%",
              border: "solid 1px #CCCCCC",
              borderRadius: "12px",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <FontAwesomeIcon
              icon={['fas', card.breadcrumb.icon as IconName]}
              style={{
                color: "#999999",
              }}
            />
            {
              card.breadcrumb.textList.map((v, i) => {
                const last = card.breadcrumb.textList.length === i + 1
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      color: last ? "#666666" : "#999999",
                    }}
                  >
                    <div>
                      {v}
                    </div>
                    {
                      !last && (
                        <FontAwesomeIcon icon={["fas", "angle-right"]} />
                      )
                    }
                  </div>
                )
              })
            }
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              backgroundColor: displayList.bgColor,
              color: displayList.color,
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            <div>
              {card.buttonText}
            </div>
            <div>
              →
            </div>
          </div>
        </div>
      </div>
    )
  }

  const buildList = (displayList: DisplayList) => {
    if (!displayArea(displayList.key)) return (<></>)
    if (!displayList.card.some((v) => displayArea(v.key))) return (<></>)
    return (
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px",
            borderBottom: "solid 1px #CCCCCC",
          }}
        >
          <div
            style={{
              backgroundColor: displayList.bgColor,
              color: displayList.color,
              width: "30px",
              height: "30px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={displayList.icon}
              alt={displayList.title}
              style={{
                width: "70%",
                filter: displayList.filter,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              gap: "2px",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                color: displayList.color,
              }}
            >
              {displayList.title}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#999999",
              }}
            >
              {displayList.description}
            </div>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {
            displayList.card.map((v) => (
              <React.Fragment key={`display_card_${v.key}`}>
                {buildCard(displayList, v)}
              </React.Fragment>
            ))
          }
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Qasee - dashboard</title>
      </Helmet>
      <section className="page_header">
        <h1>
          何を見たいですか？
        </h1>
      </section>
      <section className="page_description">
        <p>
          目的を選んで、最適なレポートへ移動します
        </p>
      </section>
      <section
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "40px",
        }}
      >
        {/* <div
          style={{
            display: "flex",
            width: "100%",
            border: "solid 1px #CCCCCC",
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div
            style={{
              padding: "16px",
              backgroundColor: "#ebf5ff",
              display: "flex",
              flexFlow: "column",
              gap: "4px",
              borderRadius: "10px 0 0 10px",
              width: "200px",
              borderRight: "solid 1px #CCCCCC",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              今日の要対応
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#666666",
              }}
            >
              {`${nowDate("YYYY/MM/DD H:mm")} 時点`}
            </div>
          </div>
          {
            STATUS_LIST.map((v, i) => (
              <React.Fragment key={`status_list_${v.key}`}>
                {buildStatus(v, STATUS_LIST.length === i + 1)}
              </React.Fragment>
            ))
          }
        </div> */}
        <div
          style={{
            display: "flex",
            flexFlow: "column",
            gap: "40px",
          }}
        >
          {
            DISPLAY_LIST.map((v) => (
              <React.Fragment key={`display_list_${v.key}`}>
                {buildList(v)}
              </React.Fragment>
            ))
          }
        </div>
      </section>
    </>
  )
}

export default Index
